import { AdAccount } from '../api/ads/ad-account.api';

// Cá nhân đặt đơn trên sàn creator (spec 2026-10-09). Quy đổi Ca PHẢI khớp backend (EscrowFunding, ruling R1):
// Ca = ceil(VND ÷ tỉ giá) theo TỪNG đơn vị ký quỹ (mỗi deal, mỗi suất sàn), tỉ giá ≤ 0 hoặc hỏng coi như 1.

export const ERROR_CODE = {
  topUpCaInsufficient: 'TOPUP_CA_INSUFFICIENT',
  ekycRequired: 'EKYC_REQUIRED',
} as const;

export type EscrowPart = { unitVnd: number; count: number };

export type OrderFunding =
  | { kind: 'Business'; balanceVnd: number | null }
  | { kind: 'Individual'; topUpCa: number | null; vndPerCa: number };

export const normalizeRate = (raw: unknown): number => {
  const n = typeof raw === 'number' ? raw : Number(raw);
  return raw === '' || raw == null || !Number.isFinite(n) || n <= 0 ? 1 : n;
};

export const caForVnd = (vnd: number, vndPerCa: number): number =>
  vnd <= 0 ? 0 : Math.ceil(vnd / normalizeRate(vndPerCa));

export const requiredCa = (parts: EscrowPart[], vndPerCa: number): number =>
  parts.reduce((sum, p) => sum + p.count * caForVnd(p.unitVnd, vndPerCa), 0);

export const requiredVnd = (parts: EscrowPart[]): number => parts.reduce((sum, p) => sum + p.count * p.unitVnd, 0);

/** Nhóm nghìn bằng dấu chấm, giống EscrowFunding.FormatCa phía backend. */
export const formatCa = (n: number): string => String(Math.trunc(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

const fmtVnd = (n: number) => `${Number(n || 0).toLocaleString('vi-VN')}đ`;

export const approxCaLabel = (parts: EscrowPart[], vndPerCa: number): string => `≈ ${formatCa(requiredCa(parts, vndPerCa))} Ca`;

export const topUpShortfallMessage = (needCa: number, topUpCa: number | null): string | null =>
  topUpCa === null || needCa <= topUpCa ? null : `Số dư Ca nạp không đủ, cần thêm ${formatCa(needCa - topUpCa)} Ca.`;

/** Câu lỗi nếu CHẮC CHẮN thiếu (doanh nghiệp: câu cũ của từng chỗ gọi); chưa biết số dư thì null, để BE quyết. */
export const fundingShortfall = (f: OrderFunding, parts: EscrowPart[], businessShortMessage: string): string | null => {
  if (f.kind === 'Individual') return topUpShortfallMessage(requiredCa(parts, f.vndPerCa), f.topUpCa);
  return f.balanceVnd !== null && requiredVnd(parts) > f.balanceVnd ? businessShortMessage : null;
};

/** Đầu câu xác nhận trước khi giữ tiền. Doanh nghiệp: y nguyên câu cũ ("Sẽ khoá … trong ví (số dư hiện tại …)"). */
export const holdNote = (f: OrderFunding, parts: EscrowPart[]): string => {
  if (f.kind === 'Individual') {
    const have = f.topUpCa !== null ? ` (đang có ${formatCa(f.topUpCa)} Ca)` : '';
    return `Sẽ giữ ${formatCa(requiredCa(parts, f.vndPerCa))} Ca nạp${have}`;
  }
  const have = f.balanceVnd !== null ? ` (số dư hiện tại ${fmtVnd(f.balanceVnd)})` : '';
  return `Sẽ khoá ${fmtVnd(requiredVnd(parts))} trong ví${have}`;
};

export const apiErrorCode = (err: unknown): string | null => {
  const code = (err as { response?: { data?: { code?: unknown } } })?.response?.data?.code;
  return typeof code === 'string' && code ? code : null;
};

export const isIndividualAccount = (a: { kind?: string | null }): boolean => a.kind === 'Individual';

/** Tài khoản DN đã xác thực (ví VND, Cab Ads DN) tách khỏi tài khoản cá nhân (mỗi người tối đa một). */
export const splitOrdererAccounts = (accounts: AdAccount[]): { business: AdAccount[]; individual: AdAccount | null } => ({
  business: accounts.filter((a) => !isIndividualAccount(a) && a.status === 'Verified'),
  individual: accounts.find((a) => isIndividualAccount(a)) ?? null,
});

export const ordererAccountLabel = (a: AdAccount): string =>
  isIndividualAccount(a) ? `Cá nhân · ${a.businessName}` : a.businessName;

/** Danh sách chọn tài khoản đặt đơn: doanh nghiệp đã xác thực trước, tài khoản cá nhân (nếu có) xếp cuối để không được chọn sẵn. */
export const orderableAccounts = (accounts: AdAccount[]): AdAccount[] => {
  const { business, individual } = splitOrdererAccounts(accounts);
  return individual ? [...business, individual] : business;
};
